import React from 'react';
import { useFormContext, useFieldArray, Controller } from 'react-hook-form';
import { TextField, Typography } from '@mui/material';
import { Button, IconButton } from 'components';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { TCustomFieldsOptions } from './CustomFieldsOptions.types';
import { TProductCustomFieldOption } from 'api/types';
import { TPhoneCaseFormValues } from '../../PhoneCaseForm.types';

const CustomFieldsOptions: TCustomFieldsOptions = ({ fieldName }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<TPhoneCaseFormValues>();

  const optionsFieldName = `${fieldName}.options` as never;

  const { fields, append, remove } = useFieldArray({
    control,
    name: optionsFieldName,
  });

  const addOption = () => {
    const newOption: TProductCustomFieldOption = {
      id: `option-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: '',
      color: '',
    };
    append(newOption);
  };

  const removeOption = (index: number) => {
    remove(index);
  };

  return (
    <div className="admin-form__options-section">
      <div className="admin-form__options-section__header">
        <Typography variant="subtitle2">Options</Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={addOption}
        >
          Add Option
        </Button>
      </div>

      {fields.length === 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          className="admin-form__options-section__empty-state"
        >
          No options added yet. Click Add Option to add one.
        </Typography>
      )}

      {fields.map((field, index) => {
        const fieldPath = fieldName.split('.');
        const fieldIndex = parseInt(fieldPath[fieldPath.length - 1] || '0', 10);
        const customFieldErrors = errors.customFields?.[fieldIndex];
        const optionsErrors = customFieldErrors?.options?.[index];

        const valueFieldName = `${fieldName}.options.${index}.name` as never;
        const colorFieldName = `${fieldName}.options.${index}.color` as never;

        return (
          <div
            key={field.id}
            className="admin-form__options-section__option-item"
          >
            <div className="admin-form__options-section__option-item__header">
              <Typography variant="body1">Option {index + 1}</Typography>
              {fields.length > 1 && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => removeOption(index)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </div>

            <div className="admin-form__field-row admin-form__field-row--half">
              <div className="admin-form__field">
                <Controller
                  name={valueFieldName}
                  control={control}
                  rules={{ required: 'Option name is required' }}
                  render={({ field: controllerField }) => (
                    <TextField
                      fullWidth
                      label="Option Name"
                      {...controllerField}
                      error={!!optionsErrors?.name}
                      helperText={optionsErrors?.name?.message}
                    />
                  )}
                />
              </div>
            </div>
            <div className="admin-form__field-row admin-form__field-row--half">
              <div className="admin-form__field">
                <Controller
                  name={colorFieldName}
                  control={control}
                  render={({ field: controllerField }) => (
                    <div className="admin-form__options-section__option-item__color-container">
                      <input
                        type="color"
                        value={controllerField.value || '#000000'}
                        onChange={(e) =>
                          controllerField.onChange(e.target.value)
                        }
                        className="admin-form__options-section__option-item__color-input"
                      />
                      <TextField
                        fullWidth
                        label="Color (Optional)"
                        value={controllerField.value || ''}
                        onChange={controllerField.onChange}
                        placeholder="#000000"
                      />
                    </div>
                  )}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CustomFieldsOptions;
